import React from 'react';
import { MissionIdParams } from '@src/core/entities';
import { useParams } from 'react-router-dom';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { ArcDroneMarker, ArcPolyline, DestinationMarker, DistanceLabel, TakeOffMarker } from '@src/core/controllers';
import DroneHomePath from '@src/features/order/pages/upsert-mission-page/components/drone-home-path';
import { RouteWaypointNumbers } from '@src/features/order/pages/upsert-mission-page/components';
import { splitHomeLeg } from '@src/features/order/pages/upsert-mission-page/utils';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';

const ChecklistMap: React.FC = () => {
  const { missionId } = useParams<MissionIdParams>();
  const { data, isLoading } = useGetOrderMissionQuery(missionId);
  const { setCameraSource } = useCameraLayout();

  const resetGoHere = React.useCallback(() => {}, []);
  const serialNumber = data?.drone?.serialNumber;

  React.useEffect(() => {
    if (serialNumber) {
      setCameraSource({
        serialNumber,
        resetGoHere,
      });
    } else {
      setCameraSource(null);
    }
  }, [serialNumber, resetGoHere]);

  const { homePoint, routeWaypoints } = React.useMemo(
    () => splitHomeLeg(data?.waypoints ?? []),
    [data],
  );

  if (isLoading || !data) return null;

  // The drone (home) position is the "HOME" waypoint when present, else the registered drone.
  const droneLat = Number(homePoint?.latitude ?? data.drone?.latitude);
  const droneLng = Number(homePoint?.longitude ?? data.drone?.longitude);

  const order = routeWaypoints.find((w) => Boolean(w.orderId));
  const takeoff = routeWaypoints.find((w) => !w.orderId) ?? routeWaypoints[0];

  const initialPath = routeWaypoints.map((w) => ({
    lat: Number(w.latitude),
    lng: Number(w.longitude),
  }));

  const takeoffLat = Number(takeoff?.latitude);
  const takeoffLng = Number(takeoff?.longitude);
  const orderLat = Number(order?.latitude);
  const orderLng = Number(order?.longitude);

  const distancePath = [
    { lat: takeoffLat, lng: takeoffLng },
    { lat: orderLat, lng: orderLng },
  ];

  return (
    <React.Fragment>
      <DestinationMarker.confirmed
        position={{
          lat: orderLat,
          lng: orderLng,
        }} />

      {!isNaN(droneLat) && !isNaN(droneLng) && (
        <ArcDroneMarker.idleSelected
          position={{
            lat: droneLat,
            lng: droneLng,
          }}
        />
      )}

      {!isNaN(takeoffLat) && !isNaN(takeoffLng) && (
        <TakeOffMarker.confirmed
          position={{ lat: takeoffLat, lng: takeoffLng }}
        />
      )}

      <ArcPolyline.future path={initialPath} />

      <RouteWaypointNumbers
        waypoints={routeWaypoints}
        takeoff={!isNaN(takeoffLat) && !isNaN(takeoffLng) ? { lat: takeoffLat, lng: takeoffLng } : null}
        destination={!isNaN(orderLat) && !isNaN(orderLng) ? { lat: orderLat, lng: orderLng } : null}
      />

      {homePoint && !isNaN(droneLat) && !isNaN(droneLng) && !isNaN(takeoffLat) && !isNaN(takeoffLng) && (
        <DroneHomePath
          from={{ lat: droneLat, lng: droneLng }}
          to={{ lat: takeoffLat, lng: takeoffLng }}
        />
      )}

      {!isNaN(takeoffLat) && !isNaN(takeoffLng) && !isNaN(orderLat) && !isNaN(orderLng) && (
        <DistanceLabel path={distancePath} />
      )}
    </React.Fragment>
  );
};

export default ChecklistMap;
